/** UK (MHRA) medical-device and IVD submission pathways — the dropdown options
 *  shown when a user picks UK at dossier dispatch time. Mirrors the CA file's
 *  shape.
 *
 *  FIRST DRAFT — labels pending regulatory review.
 *
 *  ## Devices — UK MDR 2002 (Class IIa / IIb / III)
 *
 *  The UK Medical Devices Regulations 2002 (SI 2002/618, as amended post-Brexit)
 *  use a four-class risk scheme inherited from the pre-Brexit EU MDD/IVDD.
 *  Class I is self-declared; Classes IIa, IIb, and III require conformity
 *  assessment with a UK Approved Body and a Certificate of Conformity.
 *  Class III and implantable devices also require Approved Body design
 *  examination. There is no 510(k)-style predicate finder — conformity is
 *  demonstrated in the Technical File.
 *
 *  ## IVDs — UK MDR 2002 Part IV (retained IVDD, UKCA)
 *
 *  In-vitro diagnostics in Great Britain are regulated under Part IV of SI
 *  2002/618, which retains the IVDD 98/79/EC classification scheme:
 *
 *  - General IVD (all "other" devices): self-declaration, no Approved Body —
 *    OUT OF SCOPE for this dossier generator (no submission required).
 *  - Self-test: UK Approved Body involvement required.
 *  - Annex II List B (e.g. rubella, PSA): UK Approved Body QMS + tech-doc.
 *  - Annex II List A (highest risk, e.g. ABO/Rh/Kell, HIV/hepatitis markers):
 *    UK Approved Body + design/type examination + batch verification.
 *
 *  All GB IVDs bear UKCA marking.
 *
 *  Backend engine: uk_gb_ivdr.  MCP key: "uk-mhra".  No predicate finder.
 */

import type { Product } from '@/features/product/types';
import type { SubmissionGroup } from './us-submission-pathways';

export const UK_SUBMISSION_PATHWAYS: SubmissionGroup[] = [
  {
    productType: 'device',
    label: 'Medical Device — MHRA (UK MDR 2002)',
    options: [
      { value: 'uk-class-iia', label: 'Class IIa — Conformity assessment via UK Approved Body' },
      { value: 'uk-class-iib', label: 'Class IIb — Conformity assessment via UK Approved Body' },
      { value: 'uk-class-iii', label: 'Class III — Design examination + UK Approved Body certificate' },
    ],
  },
  {
    productType: 'device',
    label: 'In-Vitro Diagnostic — MHRA (UK MDR 2002 Part IV, UKCA)',
    options: [
      { value: 'gb-ivd-self-test', label: 'Self-test — UK Approved Body (UKCA)' },
      { value: 'gb-ivd-list-b',    label: 'Annex II List B — UK Approved Body (UKCA)' },
      { value: 'gb-ivd-list-a',    label: 'Annex II List A — highest; UK Approved Body + batch verification (UKCA)' },
    ],
  },
];

/** Pre-fill the picker with the most-common pathway per product type.
 *  UK dossier scope is device + IVD; non-device types fall back to Class IIa
 *  (the lowest in-scope device class) as a safe default. */
export const UK_DEFAULT_BY_TYPE: Record<Product['product_type'], string> = {
  device:      'uk-class-iia',
  drug:        'uk-class-iia',
  biologic:    'uk-class-iia',
  combination: 'uk-class-iia',
};

/** The 3 GB IVD submission_type values that have a dossier generator.
 *  General IVD (self-declared, no Approved Body) is intentionally excluded.
 *  Centralised here so the dispatch module and tests can check membership
 *  without string duplication. */
export const GB_IVD_SUBMISSION_TYPES = new Set([
  'gb-ivd-self-test',
  'gb-ivd-list-b',
  'gb-ivd-list-a',
]);

/** Returns true when a submission_type value belongs to the GB IVD pathway. */
export function isGbIvdSubmissionType(value: string | null | undefined): boolean {
  return !!value && GB_IVD_SUBMISSION_TYPES.has(value);
}
